import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import {
  TextField,
  Box,
  Typography,
  ThemeProvider,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DrawIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import CircleIcon from '@mui/icons-material/Circle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import ImovelForm from './RuralImovelForm'; // Assuming this will be renamed or reused
import config from './config';
import { ThemeContext } from './ThemeContext';
import ThemeToggle from './ThemeToggle';

const containerStyle = {
  width: '100%',
  height: 'calc(100vh - 30px)',
};

const polygonTypes = [
  { type: 'Área Rural Total', color: '#64b5f6' }, // Changed to reflect rural land
  { type: 'Área de Cultivo', color: '#00FF00' }, // Changed to reflect agricultural use
];

const RuralApp = ({ setIsAuthenticated, initialUserData }) => {
  const { theme } = React.useContext(ThemeContext);
  const [addressInput, setAddressInput] = useState('');
  const location = useLocation();
  const calculationData = location.state?.calculationData || null;
  const [map, setMap] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [areas, setAreas] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newPolygonPath, setNewPolygonPath] = useState([]);
  const [hasUserModified, setHasUserModified] = useState(false);
  const [showAddressInput, setShowAddressInput] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [showPropriedadeForm, setShowPropriedadeForm] = useState(false); // Renamed from showImovelForm
  const [showPolygonTypeSelector, setShowPolygonTypeSelector] = useState(false);
  const [selectedPolygonType, setSelectedPolygonType] = useState(null);
  const [coordinates, setCoordinates] = useState([
    { lat: '', lng: '' },
    { lat: '', lng: '' },
    { lat: '', lng: '' },
  ]);
  const [userData, setUserData] = useState({
    polygons: initialUserData?.polygons || [],
    municipios: initialUserData?.municipios || [],
    cidades: initialUserData?.cidades || [],
    bairros: initialUserData?.bairros || [],
    enderecos: initialUserData?.enderecos || [],
  });
  const [selectedMunicipio, setSelectedMunicipio] = useState(calculationData?.id_municipio || '');
  const [selectedCidade, setSelectedCidade] = useState(calculationData?.id_cidade || '');
  const [selectedBairro, setSelectedBairro] = useState(calculationData?.id_bairro || '');
  const [selectedEndereco, setSelectedEndereco] = useState(calculationData?.id_endereco || '');
  const [fullAddress, setFullAddress] = useState('');
  const [currentMarker, setCurrentMarker] = useState(null);
  const [pdfCoordinates, setPdfCoordinates] = useState([]);
  const [openCoordinatesDialog, setOpenCoordinatesDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();

  const isAddressFilled = addressInput || (selectedMunicipio && selectedCidade);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places', 'geometry'],
  });

  // Fetch bairros and enderecos when selectedCidade or selectedBairro changes
  useEffect(() => {
    const fetchLocationData = async () => {
      if (!selectedCidade) return;

      try {
        const token = localStorage.getItem('token');
        const cidade = userData.cidades.find((c) => c.ID_CIDADE === selectedCidade)?.NOME_CIDADE;
        if (!cidade) return;

        const bairrosResponse = await axios.get(`${config.API_URL}/user-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const bairros = bairrosResponse.data.bairros.filter(
          (b) => b.ID_CIDADE === selectedCidade && b.URBANO_RURAL === 2
        );

        let enderecos = [];
        if (selectedBairro) {
          const bairro = bairros.find((b) => b.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO;
          if (bairro) {
            const regioesResponse = await axios.post(
              `${config.API_URL}/regioes`,
              { cidade, bairro },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            enderecos = regioesResponse.data.filter(endereco => endereco.URBANO_RURAL === 2);
          }
        } else {
          const regioesResponse = await axios.post(
            `${config.API_URL}/regioes`,
            { cidade, bairro: null },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          enderecos = regioesResponse.data.filter(endereco => endereco.URBANO_RURAL === 2);
        }

        setUserData((prev) => ({
          ...prev,
          bairros,
          enderecos: enderecos || [],
        }));

        if (selectedBairro && !bairros.some((b) => b.ID_BAIRRO === selectedBairro)) {
          setSelectedBairro('');
        }
        if (selectedEndereco && !enderecos.some((e) => e.ID_ENDERECO === selectedEndereco)) {
          setSelectedEndereco('');
        }
      } catch (error) {
        console.error('Erro ao buscar dados de localização:', error);
      }
    };

    fetchLocationData();
  }, [selectedCidade, selectedBairro, userData.cidades, selectedEndereco]);

  // Update fullAddress
  useEffect(() => {
    const cidade = userData.cidades.find((c) => c.ID_CIDADE === selectedCidade)?.NOME_CIDADE || '';
    const bairro = userData.bairros.find((b) => b.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO || '';
    const endereco = userData.enderecos?.find((e) => e.ID_ENDERECO === selectedEndereco)?.NOME_ENDERECO || '';
    const addressParts = [endereco, bairro, cidade, 'Brasil'].filter((part) => part);
    setFullAddress(addressParts.join(', '));
  }, [selectedMunicipio, selectedCidade, selectedBairro, selectedEndereco, userData]);

  // Restore polygons and areas from localStorage
  useEffect(() => {
    if (!map) return;

    const savedPolygons = localStorage.getItem('mapPolygons');
    const savedAreas = localStorage.getItem('mapAreas');

    if ((savedPolygons && savedPolygons !== "null") || (savedAreas && savedAreas !== "null")) {
      const parsedPolygons = JSON.parse(savedPolygons);
      const parsedAreas = JSON.parse(savedAreas);

      const restoredPolygons = parsedPolygons.map(({ paths, type, color }) => {
        const poly = new window.google.maps.Polygon({
          paths: paths.map(({ lat, lng }) => ({ lat, lng })),
          editable: true,
          draggable: true,
          fillColor: color,
          strokeColor: color,
          strokeWeight: 1,
          map,
        });

        poly.getPath().addListener('set_at', () => {
          calculateArea(poly, type);
        });
        poly.getPath().addListener('insert_at', () => {
          calculateArea(poly, type);
        });

        return { poly, type, color };
      });

      setPolygons(restoredPolygons);
      setAreas(parsedAreas);
    }
  }, [map]);

  // Save polygons and areas to localStorage whenever they change
  useEffect(() => {
    if (!hasUserModified) {
      setHasUserModified(true);
      const savedPolygons = localStorage.getItem('mapPolygons');
      const savedAreas = localStorage.getItem('mapAreas');

      if ((savedPolygons && savedPolygons !== "null") || (savedAreas && savedAreas !== "null")) {
        const parsedPolygons = JSON.parse(savedPolygons);
        const parsedAreas = JSON.parse(savedAreas);

        // Recreate Google Maps polygon objects
        const restoredPolygons = parsedPolygons.map(({ paths, type, color }) => {
          const poly = new window.google.maps.Polygon({
            paths: paths.map(({ lat, lng }) => ({ lat, lng })),
            editable: true,
            draggable: true,
            fillColor: color,
            strokeColor: color,
            strokeWeight: 1,
            map,
          });

          // Add listeners for polygon edits
          poly.getPath().addListener('set_at', () => {
            calculateArea(poly, type);
          });
          poly.getPath().addListener('insert_at', () => {
            calculateArea(poly, type);
          });

          return { poly, type, color };
        });

        setPolygons(restoredPolygons);
        setAreas(parsedAreas);
      }
      return;
    }

    if (polygons.length === 0 && areas.length === 0) {
      localStorage.removeItem('mapPolygons');
      localStorage.removeItem('mapAreas');
      return;
    }

    // Serialize polygons to a JSON-compatible format
    const serializablePolygons = polygons.map(({ poly, type, color }) => ({
      paths: poly.getPath().getArray().map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      })),
      type,
      color,
    }));

    localStorage.setItem('mapPolygons', JSON.stringify(serializablePolygons));
    localStorage.setItem('mapAreas', JSON.stringify(areas));
  }, [polygons, areas, hasUserModified, map]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userData');
          setIsAuthenticated(false);
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, setIsAuthenticated]);

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData && !initialUserData) {
      setUserData(JSON.parse(storedUserData));
    }
  }, [initialUserData]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const cidade = userData.cidades.find((c) => c.ID_CIDADE === selectedCidade)?.NOME_CIDADE || '';
    const bairro = userData.bairros.find((b) => b.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO || '';
    const addressParts = [bairro, cidade, 'Brasil'].filter((part) => part);
    setFullAddress(addressParts.join(', '));
  }, [selectedMunicipio, selectedCidade, selectedBairro, userData]);

  const filteredCidades = userData.cidades.filter((cidade) => cidade.ID_MUNICIPIO === selectedMunicipio);
  const filteredBairros = userData.bairros.filter((bairro) => bairro.ID_CIDADE === selectedCidade);

  const onLoad = async (mapInstance) => {
    try {
      const newVector = await getInitialCenter();
      setCenter(newVector);
      setMap(mapInstance);
    } catch (error) {
      console.error("Error in onLoad:", error);
    }
  };

  const [center, setCenter] = useState({ lat: -23.5505, lng: -46.6333 });

  // Função para geocodificar um endereço inicial, como 'São Paulo, Brasil'
  const getInitialCenter = async () => {
    let storedAddress = fullAddress;
    const polygonsData = JSON.parse(localStorage.getItem('mapPolygons'));
    const firstItem = (polygonsData && polygonsData.length > 0) ? polygonsData[0] : null;
    if(firstItem){
      storedAddress = firstItem;
    }
    if (storedAddress === 'Brasil') {
      storedAddress = localStorage.getItem('dados');
    }
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address: storedAddress,
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          },
        }
      );
      const results = response.data.results;
      if (results && results.length > 0) {
        return results[0].geometry.location; // Retorna { lat, lng }
      } else {
        console.error('Endereço inicial não encontrado, usando fallback.');
        return { lat: -23.5505, lng: -46.6333 }; // Fallback caso o endereço não seja encontrado
      }
    } catch (error) {
      console.error('Erro ao geocodificar endereço inicial:', error);
      return { lat: -23.5505, lng: -46.6333 }; // Fallback em caso de erro
    }
  };

  const geocodeAddress = async () => {
    if (!addressInput && !fullAddress) {
      alert('Por favor, insira informações de endereço ou selecione opções nos dropdowns.');
      return;
    }
    try {
      const addressParts = [addressInput, fullAddress].filter((part) => part);
      const addressToGeocode = addressParts.join(', ');
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address: addressToGeocode,
            key: 'AIzaSyD_G0dtBimWGZoGd2bxiSkrZ-a6KaHVTj4',
          },
        }
      );
      const results = response.data.results;
      if (results && results.length > 0) {
        const location = results[0].geometry.location;

        if (currentMarker) {
          currentMarker.setMap(null);
        }

        const newMarker = new window.google.maps.Marker({
          map,
          position: location,
        });

        setCurrentMarker(newMarker);
        map.setCenter(location);
        map.setZoom(15);
        setShowAddressInput(false);
      } else {
        alert('Endereço não encontrado');
      }
    } catch (error) {
      console.error('Erro na geocodificação:', error.response || error.message);
      alert('Erro na geocodificação: ' + (error.response?.data?.error_message || error.message));
    }
  };

  const handleMapClick = (event) => {
    if (!isDrawing || !event?.latLng || !selectedPolygonType) {
      return;
    }

    setNewPolygonPath((prev) => [
      ...prev,
      { lat: event.latLng.lat(), lng: event.latLng.lng() },
    ]);
  };

  const toggleDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setShowPolygonTypeSelector(false);

      if (newPolygonPath.length > 2 && selectedPolygonType) {
        const newPolygon = new window.google.maps.Polygon({
          paths: newPolygonPath,
          editable: true,
          draggable: true,
          fillColor: selectedPolygonType.color,
          strokeColor: selectedPolygonType.color,
          strokeWeight: 1,
        });
        onPolygonComplete(newPolygon);
      }

      setNewPolygonPath([]);
    } else {
      setShowPolygonTypeSelector(!showPolygonTypeSelector);
    }
  };

  const selectPolygonType = (type, color) => {
    setSelectedPolygonType({ type, color });
    setIsDrawing(true);
    setNewPolygonPath([]);
    setShowPolygonTypeSelector(false);
  };

  const onPolygonComplete = (poly) => {
    if (selectedPolygonType) {
      const existingPolygonIndex = polygons.findIndex((p) => p.type === selectedPolygonType.type);
      if (existingPolygonIndex !== -1) {
        polygons[existingPolygonIndex].poly.setMap(null);
        setPolygons(polygons.filter((p) => p.type !== selectedPolygonType.type));
        setAreas(areas.filter((a) => a.type !== selectedPolygonType.type));
      }

      poly.setOptions({
        fillColor: selectedPolygonType.color,
        strokeColor: selectedPolygonType.color,
        editable: true,
        draggable: true,
      });

      poly.getPath().addListener('set_at', () => {
        calculateArea(poly, selectedPolygonType.type);
      });
      poly.getPath().addListener('insert_at', () => {
        calculateArea(poly, selectedPolygonType.type);
      });

      setPolygons([...polygons.filter((p) => p.type !== selectedPolygonType.type), { poly, type: selectedPolygonType.type, color: selectedPolygonType.color }]);
      calculateArea(poly, selectedPolygonType.type);
    }
    setIsDrawing(false);
    setNewPolygonPath([]);
    setSelectedPolygonType(null);
  };

  const calculateArea = async (poly, type) => {
    if (poly) {
      const calculatedArea = window.google.maps.geometry.spherical.computeArea(poly.getPath());
      setAreas((prevAreas) => [
        ...prevAreas.filter((a) => a.type !== type),
        { area: calculatedArea.toFixed(2), type },
      ]);
    }
  };

  const clearPolygon = (index) => {
    if (polygons[index]) {
      polygons[index].poly.setMap(null);
      const newPolygons = polygons.filter((_, i) => i !== index);
      const newAreas = areas.filter((_, i) => i !== index);
      setPolygons(newPolygons);
      setAreas(newAreas);
    }
  };

  const addCoordinate = () => {
    setCoordinates([...coordinates, { lat: '', lng: '' }]);
  };

  const removeCoordinate = (index) => {
    const newCoordinates = coordinates.filter((_, i) => i !== index);
    setCoordinates(newCoordinates.length > 3 ? newCoordinates : coordinates);
  };

  const updateCoordinate = (index, field, value) => {
    const newCoordinates = [...coordinates];
    newCoordinates[index][field] = value;
    setCoordinates(newCoordinates);
  };

  const centerOnFirstCoordinate = () => {
    const firstValidCoord = coordinates.find(
      (coord) => coord.lat && coord.lng && !isNaN(coord.lat) && !isNaN(coord.lng)
    );
    if (firstValidCoord && map) {
      const location = {
        lat: parseFloat(firstValidCoord.lat),
        lng: parseFloat(firstValidCoord.lng),
      };
      map.setCenter(location);
      map.setZoom(15);
    } else {
      alert('Insira pelo menos uma coordenada válida para centralizar o mapa.');
    }
  };

  const handlePDFUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = reader.result.split(',')[1];
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${config.API_URL}/extract-pdf-text`,
            { base64_pdf: base64String },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setPdfCoordinates(response.data);
          setOpenCoordinatesDialog(true);
        } catch (error) {
          console.error('Erro ao extrair coordenadas do PDF:', error.response || error.message);
        }
      };
      reader.readAsDataURL(file);
    } else {
      alert('Por favor, selecione um arquivo PDF válido.');
    }
  };

  const updatePdfCoordinate = (index, field, value) => {
    const newCoordinates = [...pdfCoordinates];
    newCoordinates[index][field] = value;
    setPdfCoordinates(newCoordinates);
  };

  const convertToUTM = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.API_URL}/convert-to-utm`,
        { coordinates: pdfCoordinates },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const utmCoordinates = response.data;
      setOpenCoordinatesDialog(false);

      if (utmCoordinates.length >= 3) {
        const newPolygon = new window.google.maps.Polygon({
          paths: utmCoordinates.map((coord) => ({
            lat: parseFloat(coord.latitude),
            lng: parseFloat(coord.longitude),
          })),
          editable: true,
          draggable: true,
          fillColor: '#64b5f6',
          strokeColor: '#64b5f6',
          strokeWeight: 1,
          map: map,
        });

        newPolygon.getPath().addListener('set_at', () => {
          calculateArea(newPolygon, 'Área Rural Total');
        });
        newPolygon.getPath().addListener('insert_at', () => {
          calculateArea(newPolygon, 'Área Rural Total');
        });

        setPolygons((prev) => [
          ...prev,
          { poly: newPolygon, type: 'Área Rural Total', color: '#64b5f6' },
        ]);
        calculateArea(newPolygon, 'Área Rural Total');
        map.setCenter({
          lat: parseFloat(utmCoordinates[0].latitude),
          lng: parseFloat(utmCoordinates[0].longitude),
        });
        map.setZoom(15);
      }
    } catch (error) {
      console.error('Erro ao converter para UTM:', error.response || error.message);
      alert('Erro ao converter coordenadas: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetAll = () => {
    if (!window.confirm('Tem certeza que deseja resetar todos os dados?')) {
      return;
    }

    polygons.forEach(({ poly }) => poly.setMap(null));
    setPolygons([]);
    setAreas([]);

    if (currentMarker) {
      currentMarker.setMap(null);
      setCurrentMarker(null);
    }

    setAddressInput('');
    setSelectedMunicipio(userData.municipios?.[0]?.ID_MUNICIPIO || '');
    setSelectedCidade('');
    setSelectedBairro('');
    setFullAddress('');

    setCoordinates([
      { lat: '', lng: '' },
      { lat: '', lng: '' },
      { lat: '', lng: '' },
    ]);
    setPdfCoordinates([]);

    setShowAddressInput(false);
    setShowCoordinates(false);
    setShowPropriedadeForm(false);
    setShowPolygonTypeSelector(false);
    setIsDrawing(false);
    setNewPolygonPath([]);
    setSelectedPolygonType(null);

    if (map) {
      map.setCenter(center);
      map.setZoom(12);
    }

    localStorage.removeItem('propriedadeFormData'); // Changed from imovelFormData
    localStorage.removeItem('mapPolygons');
    localStorage.removeItem('mapAreas');
  };

  useEffect(() => {
    if (!map || !selectedPolygonType) return;

    const validCoordinates = coordinates
      .filter((coord) => coord.lat && coord.lng && !isNaN(coord.lat) && !isNaN(coord.lng))
      .map((coord) => ({
        lat: parseFloat(coord.lat),
        lng: parseFloat(coord.lng),
      }));

    if (validCoordinates.length < 3) {
      return;
    }

    const newPolygon = new window.google.maps.Polygon({
      paths: validCoordinates,
      editable: true,
      draggable: true,
      fillColor: selectedPolygonType.color,
      strokeColor: selectedPolygonType.color,
      strokeWeight: 1,
      map: map,
    });

    newPolygon.getPath().addListener('set_at', () => {
      calculateArea(newPolygon, selectedPolygonType.type);
    });
    newPolygon.getPath().addListener('insert_at', () => {
      calculateArea(newPolygon, selectedPolygonType.type);
    });

    setPolygons((p) => [...p, { type: selectedPolygonType.type, color: selectedPolygonType.color, poly: newPolygon }]);
    calculateArea(newPolygon, selectedPolygonType.type);
  }, [coordinates, map, selectedPolygonType]);

  useEffect(() => {
    if (userData.municipios && userData.municipios.length > 0 && !selectedMunicipio) {
      setSelectedMunicipio(userData.municipios[0].ID_MUNICIPIO);
    }
  }, [userData.municipios, selectedMunicipio]);

  const totalArea = areas.reduce((sum, { area }) => sum + parseFloat(area), 0).toFixed(2);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('propriedadeFormData'); // Changed from imovelFormData
    localStorage.removeItem('mapPolygons');
    localStorage.removeItem('mapAreas');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const handleBackToSelection = () => {
    navigate('/selection');
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', maxHeight: '100vh', backgroundColor: 'background.default' }}>
        <Box
          sx={{
            width: 60,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            gap: 2,
          }}
        >
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
              Login expirado, favor reconectar.
            </Alert>
          </Snackbar>
          <Tooltip title="Voltar para Seleção" placement="right">
            <IconButton onClick={handleBackToSelection} sx={{ mb: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Selecionar Endereço" placement="right">
            <IconButton
              onClick={() => {
                setShowAddressInput(!showAddressInput);
                setShowPropriedadeForm(false);
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dados da Propriedade Rural" placement="right"> {/* Changed tooltip text */}
            <IconButton
              onClick={() => {
                setShowPropriedadeForm(!showPropriedadeForm);
                setShowAddressInput(false);
                setShowCoordinates(false);
              }}
              disabled={!isAddressFilled}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload de PDF" placement="right">
            <IconButton component="label" disabled={showPropriedadeForm}>
              <UploadFileIcon />
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={handlePDFUpload}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={isDrawing ? 'Parar Desenho' : 'Desenhar Polígono'}
            placement="right"
          >
            <IconButton onClick={toggleDrawing} disabled={showPropriedadeForm}>
              <DrawIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Inserir Coordenadas" placement="right">
            <IconButton
              onClick={() => {
                setShowCoordinates(!showCoordinates);
                setShowPropriedadeForm(false);
                setShowPolygonTypeSelector(!showPolygonTypeSelector);
              }}
              disabled={showPropriedadeForm}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Total" placement="right">
            <IconButton onClick={resetAll} disabled={showPropriedadeForm}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sair" placement="right">
            <IconButton onClick={handleLogout} sx={{ mb: 2 }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          <ThemeToggle sx={{ mt: 'auto' }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {loadError ? (
            <Typography color="error">Error loading Google Maps: {loadError.message}</Typography>
          ) : !isLoaded ? (
            <Typography>Loading Google Maps...</Typography>
          ) : showPropriedadeForm ? (
            <ImovelForm
              key={areas.length}
              onClose={() => setShowPropriedadeForm(false)}
              calculatedArea={areas.find(a => a.type === 'Área Rural Total')?.area || calculationData?.area_lote || null}
              constructionArea={areas.find(a => a.type === 'Área de Cultivo')?.area || calculationData?.area_construida || null}
              addressInput={addressInput}
              selectedMunicipio={selectedMunicipio}
              selectedCidade={selectedCidade}
              selectedBairro={selectedBairro}
              selectedEndereco={selectedEndereco}
              userData={userData}
            />
          ) : (
            <>
              <Collapse in={showPolygonTypeSelector}>
                <Box
                  sx={{
                    maxWidth: 600,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    bgcolor: 'background.paper',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                    Selecionar Tipo de Polígono
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    {polygonTypes.map(({ type, color }) => (
                      <Button
                        key={type}
                        variant="contained"
                        sx={{
                          backgroundColor: color,
                          '&:hover': {
                            backgroundColor: color,
                            opacity: 0.9,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          },
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'medium',
                          px: 3,
                          py: 1,
                          transition: 'all 0.3s ease',
                          minWidth: 120,
                        }}
                        onClick={() => selectPolygonType(type, color)}
                      >
                        {type}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Collapse>
              <Collapse in={showAddressInput}>
                <Box
                  sx={{
                    maxWidth: 400,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Selecionar Endereço
                  </Typography>
                  <FormControl variant="outlined" fullWidth disabled={!selectedMunicipio}>
                    <InputLabel>Cidade</InputLabel>
                    <Select
                      value={selectedCidade}
                      onChange={(e) => {
                        setSelectedCidade(e.target.value);
                        setSelectedBairro('');
                      }}
                      label="Cidade"
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {filteredCidades.map((cidade) => (
                        <MenuItem key={cidade.ID_CIDADE} value={cidade.ID_CIDADE}>
                          {cidade.NOME_CIDADE}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="outlined" fullWidth disabled={!selectedCidade}>
                    <InputLabel>Localização</InputLabel>
                    <Select
                      value={selectedBairro}
                      onChange={(e) => {
                        setSelectedBairro(e.target.value);
                      }}
                      label="Bairro"
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {filteredBairros.map((bairro) => (
                        <MenuItem key={bairro.ID_BAIRRO} value={bairro.ID_BAIRRO}>
                          {bairro.NOME_BAIRRO}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Endereço (rua, número, complemento)"
                    variant="outlined"
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && geocodeAddress()}
                    autoFocus
                  />
                  {fullAddress && (
                    <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                      Endereço Completo: {fullAddress}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={geocodeAddress}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    Buscar Endereço
                  </Button>
                </Box>
              </Collapse>
              <Collapse in={showCoordinates}>
                <Box
                  sx={{
                    maxWidth: 400,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Coordenadas do Polígono
                  </Typography>
                  {coordinates.map((coord, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      <TextField
                        label={`Latitude ${index + 1}`}
                        variant="outlined"
                        value={coord.lat}
                        onChange={(e) => updateCoordinate(index, 'lat', e.target.value)}
                        size="small"
                        type="number"
                        sx={{ width: 150 }}
                      />
                      <TextField
                        label={`Longitude ${index + 1}`}
                        variant="outlined"
                        value={coord.lng}
                        onChange={(e) => updateCoordinate(index, 'lng', e.target.value)}
                        size="small"
                        type="number"
                        sx={{ width: 150 }}
                      />
                      {coordinates.length > 3 && (
                        <IconButton onClick={() => removeCoordinate(index)}>
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addCoordinate}
                      sx={{ flex: 1 }}
                    >
                      Adicionar Ponto
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<MyLocationIcon />}
                      onClick={centerOnFirstCoordinate}
                      sx={{ flex: 1 }}
                    >
                      Centralizar no Primeiro Ponto
                    </Button>
                  </Box>
                </Box>
              </Collapse>
              {areas.length > 0 && (
                <Box
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 1,
                    p: 1.5,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 'medium',
                      fontSize: '1rem',
                      flexShrink: 0,
                    }}
                  >
                    Áreas dos Polígonos:
                  </Typography>
                  <List
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 0,
                      flexGrow: 1,
                      flexWrap: 'nowrap',
                    }}
                  >
                    {areas.map(({ area, type }, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          width: 'auto',
                          borderRadius: 1,
                          py: 0.25,
                          px: 0.75,
                          '&:hover': {
                            bgcolor: 'action.hover',
                            transition: 'background-color 0.3s ease',
                          },
                        }}
                        secondaryAction={
                          <IconButton edge="end" onClick={() => clearPolygon(index)} size="small">
                            <ClearIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 2, mr: 0.5 }}>
                          <CircleIcon
                            sx={{
                              color: polygonTypes.find((pt) => pt.type === type)?.color || '#90caf9',
                              fontSize: 10,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${type}: ${area} m²`}
                          primaryTypographyProps={{
                            color: 'text.primary',
                            fontSize: '0.85rem',
                            fontWeight: 'regular',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      bgcolor: 'divider',
                      width: '1px',
                      mx: 1,
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontWeight: 'medium',
                        fontSize: '0.9rem',
                      }}
                    >
                      Área Total: {totalArea} m²
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ flex: 1, borderRadius: '12px', overflow: 'hidden' }}>
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={center}
                  zoom={12}
                  onLoad={onLoad}
                  onClick={handleMapClick}
                >
                  {isDrawing && newPolygonPath.length > 0 && (
                    <Polyline
                      path={newPolygonPath}
                      options={{
                        strokeColor: selectedPolygonType?.color || '#64b5f6',
                        strokeOpacity: 1.0,
                        strokeWeight: 2,
                      }}
                    />
                  )}
                </GoogleMap>
              </Box>
              <Dialog
                open={openCoordinatesDialog}
                onClose={() => setOpenCoordinatesDialog(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>Coordenadas Extraídas do PDF</DialogTitle>
                <DialogContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Latitude</TableCell>
                          <TableCell>Longitude</TableCell>
                          <TableCell>Altitude</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pdfCoordinates.map((coord, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                value={coord.latitude}
                                onChange={(e) =>
                                  updatePdfCoordinate(index, 'latitude', e.target.value)
                                }
                                size="small"
                                type="text"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={coord.longitude}
                                onChange={(e) =>
                                  updatePdfCoordinate(index, 'longitude', e.target.value)
                                }
                                size="small"
                                type="text"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={coord.altitude}
                                onChange={(e) =>
                                  updatePdfCoordinate(index, 'altitude', e.target.value)
                                }
                                size="small"
                                type="text"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenCoordinatesDialog(false)}>Cancelar</Button>
                  <Button onClick={convertToUTM} variant="contained">
                    Converter para UTM
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default RuralApp;